import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useConversation } from '@11labs/react';
import {
  Mic,
  MicOff,
  PhoneOff,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Bot,
  User,
  Clock,
  Sparkles,
} from 'lucide-react';
import clsx from 'clsx';
import type { AssessmentProfile, TranscriptMessage } from '../types';
import { createSession, updateSessionProfile, completeSession } from '../lib/api';
import { getDepartment } from '../config/departments';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

type Phase = 'idle' | 'connecting' | 'active' | 'generating' | 'done';

const GENERATING_MESSAGES = [
  'Analyzing your responses...',
  'Mapping your current process...',
  'Benchmarking against industry peers...',
  'Identifying key bottlenecks...',
  'Calculating maturity scores...',
  'Crafting personalized recommendations...',
  'Building your technology landscape...',
  'Finalizing your report...',
];

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function MicVisualizer({ active }: { active: boolean }) {
  return (
    <div className="flex items-end gap-1 h-8">
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className={clsx(
            'w-1.5 rounded-full transition-all duration-300',
            active ? `bg-[#0C91C6] bar-${i}` : 'bg-[#D8EBFE] h-1'
          )}
          style={{ height: active ? undefined : '4px' }}
        />
      ))}
    </div>
  );
}

function StatusDot({ phase }: { phase: Phase }) {
  const config: Record<Phase, { color: string; label: string }> = {
    idle: { color: 'bg-[#999]', label: 'Ready' },
    connecting: { color: 'bg-yellow-400 animate-pulse', label: 'Connecting...' },
    active: { color: 'bg-green-400 pulse-glow-green', label: 'Live' },
    generating: { color: 'bg-[#0C91C6] animate-pulse', label: 'Generating' },
    done: { color: 'bg-green-400', label: 'Complete' },
  };
  const { color, label } = config[phase];
  return (
    <div className="flex items-center gap-2">
      <div className={clsx('w-2.5 h-2.5 rounded-full', color)} />
      <span className="text-sm font-medium text-[#666666]">{label}</span>
    </div>
  );
}

function TranscriptBubble({ msg }: { msg: TranscriptMessage }) {
  const isAgent = msg.role === 'agent';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={clsx('flex gap-3 max-w-[85%]', isAgent ? 'self-start' : 'self-end flex-row-reverse')}
    >
      <div
        className={clsx(
          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
          isAgent
            ? 'bg-[#E8F4FC] border border-[#D8EBFE]'
            : 'bg-[#F5FAFD] border border-[#D8EBFE]'
        )}
      >
        {isAgent ? (
          <Bot size={14} className="text-[#004AA9]" />
        ) : (
          <User size={14} className="text-[#666666]" />
        )}
      </div>

      <div className="flex flex-col gap-1">
        <div
          className={clsx(
            'px-4 py-3 rounded-2xl text-sm leading-relaxed',
            isAgent
              ? 'bg-[#F5FAFD] border border-[#D8EBFE] text-[#333333] rounded-tl-sm'
              : 'bg-[#E8F4FC] border border-[#B8D4F0] text-[#1A1A2E] rounded-tr-sm'
          )}
        >
          {msg.text}
        </div>
        <span
          className={clsx(
            'text-xs text-[#999]',
            isAgent ? 'text-left pl-1' : 'text-right pr-1'
          )}
        >
          {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </motion.div>
  );
}

function GeneratingOverlay({ visible }: { visible: boolean }) {
  const [msgIndex, setMsgIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!visible) return;
    const msgInterval = setInterval(() => {
      setMsgIndex((i) => (i + 1) % GENERATING_MESSAGES.length);
    }, 4000);
    const start = Date.now();
    const progressInterval = setInterval(() => {
      const elapsedMs = Date.now() - start;
      let pct: number;
      if (elapsedMs < 30000) {
        pct = (elapsedMs / 30000) * 80;
      } else {
        const extra = (elapsedMs - 30000) / 120000;
        pct = 80 + Math.min(extra * 18, 18);
      }
      setProgress(Math.min(pct, 98));
    }, 200);
    return () => {
      clearInterval(msgInterval);
      clearInterval(progressInterval);
    };
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/95 backdrop-blur-sm"
        >
          <div className="relative mb-8">
            <div className="w-24 h-24 rounded-full border-2 border-[#D8EBFE]" />
            <svg
              className="absolute inset-0 w-24 h-24 -rotate-90 animate-spin-slow"
              viewBox="0 0 96 96"
            >
              <circle
                cx="48"
                cy="48"
                r="46"
                fill="none"
                stroke="url(#grad)"
                strokeWidth="2"
                strokeDasharray="72 217"
                strokeLinecap="round"
              />
              <defs>
                <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#0B3D91" />
                  <stop offset="100%" stopColor="#0C91C6" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <Sparkles size={28} className="text-[#004AA9]" />
            </div>
          </div>

          <div className="h-8 overflow-hidden mb-2">
            <AnimatePresence mode="wait">
              <motion.p
                key={msgIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
                className="text-xl font-heading font-semibold text-[#004AA9] text-center"
              >
                {GENERATING_MESSAGES[msgIndex]}
              </motion.p>
            </AnimatePresence>
          </div>

          <p className="text-[#999] text-sm mb-10">This usually takes 30–60 seconds</p>

          <div className="w-64 h-1.5 bg-[#E8F4FC] rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full shimmer"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
          <p className="text-[#999] text-xs mt-2">{Math.round(progress)}%</p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function VoiceAssessmentPage() {
  const { department: deptParam = 'general' } = useParams<{ department?: string }>();
  const navigate = useNavigate();
  const dept = getDepartment(deptParam);

  const [phase, setPhase] = useState<Phase>('idle');
  const [transcript, setTranscript] = useState<TranscriptMessage[]>([]);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [showGenerateButton, setShowGenerateButton] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assessmentDataRef = useRef<AssessmentProfile>({});
  const transcriptRef = useRef<TranscriptMessage[]>([]);
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const phaseRef = useRef<Phase>('idle');
  // Mirror sessionToken in a ref so the ElevenLabs SDK callbacks (captured once)
  // always read the current token, not a stale null from first render.
  const sessionTokenRef = useRef<string | null>(null);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  // ── Timer — counts call duration ──────────────────────────
  useEffect(() => {
    if (phase === 'active') {
      timerRef.current = setInterval(() => {
        setCallDuration((d) => {
          if (d + 1 >= 120) setShowGenerateButton(true);
          return d + 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [phase]);

  // ── Auto-end at 615 seconds (10m15s) safety net ───────────
  // Fires just after ElevenLabs max duration message plays (~10min)
  // Ensures report always generates even if onDisconnect doesn't fire
  useEffect(() => {
    if (phase === 'active' && callDuration >= 615) {
      endConversation();
    }
  }, [callDuration, phase]);

  useEffect(() => {
    createSession(deptParam)
      .then((session) => {
        setSessionToken(session.id);
        sessionTokenRef.current = session.id;
      })
      .catch((err) => {
        console.warn('Could not create session (backend may not be running):', err);
        const local = `local-${Date.now()}`;
        setSessionToken(local);
        sessionTokenRef.current = local;
      });
  }, [deptParam]);

  const pollForResults = useCallback(
    (token: string) => {
      const maxAttempts = 30;
      let attempts = 0;

      const poll = async () => {
        attempts++;
        try {
          const res = await fetch(`${API_URL}/api/results/${token}/preview`);
          const data = await res.json();
          if (data.results_ready) {
            navigate(`/results/${token}`);
            return;
          }
          // Results not ready — (re)trigger generation. /generate is idempotent
          // (returns cached results if already generated). Fire it once, early,
          // so a failed primary call still gets the report produced.
          if (!data.results_ready && attempts === 1) {
            console.warn('Results not ready, triggering generation...');
            try {
              const retryRes = await fetch(`${API_URL}/api/results/${token}/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: token }),
              });
              const retryData = await retryRes.json();
              if (retryData.results_ready) {
                navigate(`/results/${token}`);
                return;
              }
            } catch {
              // Retry failed, keep polling
            }
          }
        } catch {
          // Backend not available
        }
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          navigate(`/results/${token}`);
        }
      };

      setTimeout(poll, 3000);
    },
    [navigate]
  );

  // ── Always trigger report on call end ─────────────────────
  const handleEndConversation = useCallback(async () => {
    if (phaseRef.current === 'generating' || phaseRef.current === 'done') return;

    setPhase('generating');

    // Read from the ref, not state — this callback is captured by the
    // ElevenLabs SDK and would otherwise see a stale null token, silently
    // skipping /complete + /generate and leaving the UI stuck at 98%.
    const token = sessionTokenRef.current;
    if (!token) {
      console.warn('No session token at end of conversation — cannot generate.');
      return;
    }

    try {
      await completeSession(token, assessmentDataRef.current);
    } catch (err) {
      console.warn('Could not complete session:', err);
    }

    // Primary path: drive generation directly and await it. It runs the LLM
    // inside the request (reliable on Cloud Run) and always resolves — the
    // backend falls back to a mock on timeout — so it never hangs at 98%.
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 100000);
      const res = await fetch(`${API_URL}/api/results/${token}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: token }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        const data = await res.json();
        if (data.results_ready) {
          navigate(`/results/${token}`);
          return;
        }
      }
    } catch (err) {
      console.warn('Direct generation failed, falling back to polling:', err);
    }

    // Fallback: poll (and re-trigger generation) if the direct call failed.
    pollForResults(token);
  }, [pollForResults, navigate]);

  const conversation = useConversation({
    onConnect: () => {
      console.log('[11labs] onConnect fired');
      setPhase('active');
      setError(null);
    },
    onDisconnect: (details: unknown) => {
      console.log('[11labs] onDisconnect fired — phase:', phaseRef.current, 'details:', details);
      const d = details as { reason?: string; message?: string } | undefined;
      if (d?.reason === 'error' && d?.message) {
        console.error('[11labs] Server error:', d.message);
        setError(`Voice service error: ${d.message}`);
        setPhase('idle');
        return;
      }
      if (phaseRef.current === 'active') {
        handleEndConversation();
      }
    },
    onMessage: ({ message, source }: { message: string; source: string }) => {
      const msg: TranscriptMessage = {
        role: source === 'ai' ? 'agent' : 'user',
        text: message,
        timestamp: new Date(),
      };
      transcriptRef.current = [...transcriptRef.current, msg];
      setTranscript((prev) => [...prev, msg]);
    },
    onError: (err: unknown) => {
      console.error('[11labs] onError:', err);
      setError('Connection error. Please try again.');
      setPhase('idle');
    },
    onAudio: () => {
      // Required by SDK
    },
    clientTools: {
      capture_assessment_data: async (params: Record<string, unknown>) => {
        assessmentDataRef.current = { ...assessmentDataRef.current, ...params };
        if (sessionToken) {
          try {
            await updateSessionProfile(sessionToken, assessmentDataRef.current);
          } catch {
            // Ignore backend errors
          }
        }
        return 'Data captured successfully';
      },
      assessment_complete: async (params: Record<string, unknown>) => {
        assessmentDataRef.current = { ...assessmentDataRef.current, ...params };
        setTimeout(() => handleEndConversation(), 1000);
        return 'Assessment complete, generating results';
      },
    },
  });

  async function startConversation() {
    const agentId = dept.agentId !== 'default'
      ? dept.agentId
      : import.meta.env.VITE_ELEVENLABS_AGENT_ID;
    if (!agentId || agentId === 'default' || agentId === 'your_agent_id_here') {
      setError('ElevenLabs Agent ID not configured. Set VITE_ELEVENLABS_AGENT_ID in your .env file.');
      return;
    }
    setPhase('connecting');
    setError(null);
    setTranscript([]);
    transcriptRef.current = [];
    assessmentDataRef.current = {};
    setCallDuration(0);
    setShowGenerateButton(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      console.log('[mic] Microphone permission confirmed');
    } catch (micErr) {
      console.error('[mic] Microphone access denied:', micErr);
      setError(
        'Microphone access is required for the voice assessment. ' +
        'Please allow microphone access in your browser settings and try again.'
      );
      setPhase('idle');
      return;
    }

    try {
      await conversation.startSession({
        agentId,
        dynamicVariables: {
          session_id: sessionTokenRef.current ?? '',
        },
      });
    } catch (err) {
      console.error('Failed to start conversation:', err);
      setError('Failed to connect. Check your ElevenLabs configuration.');
      setPhase('idle');
    }
  }

  async function endConversation() {
    try {
      await conversation.endSession();
    } catch {
      // Force end anyway
    }
    handleEndConversation();
  }

  const isActive = phase === 'active';
  const isConnecting = phase === 'connecting';
  const isIdle = phase === 'idle';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <GeneratingOverlay visible={phase === 'generating'} />

      <header className="flex items-center justify-between px-6 py-4 border-b border-[#D8EBFE] bg-white">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#666666] hover:text-[#004AA9] transition-colors text-sm"
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#0B3D91]" />
          <span className="text-[#666666] text-sm font-medium">{dept.name}</span>
        </div>
        <div className="w-16" />
      </header>

      <div className="flex flex-1 overflow-hidden">
        <div className="w-full md:w-[380px] flex-shrink-0 flex flex-col border-r border-[#D8EBFE] p-6 gap-6 bg-[#F5FAFD]">
          <div className="flex items-center justify-between">
            <StatusDot phase={phase} />
            {(isActive || phase === 'generating') && (
              <div className="flex items-center gap-1.5 text-[#666666] text-sm font-mono">
                <Clock size={14} />
                {formatTime(callDuration)}
              </div>
            )}
          </div>

          {isIdle && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-5 rounded-[24px] bg-white border-2 border-[#D8EBFE] shadow-nera"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#0B3D91]">
                  <Bot size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-[#004AA9] font-heading font-semibold text-sm">AI Consultant</p>
                  <p className="text-[#999] text-xs">Ready to assess</p>
                </div>
              </div>
              <p className="text-[#666666] font-body text-sm leading-relaxed">
                I'll ask you about your current processes, tools, and challenges over the next
                10 minutes. Speak naturally. There are no wrong answers.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {dept.processAreas.map((area) => (
                  <span
                    key={area}
                    className="text-xs px-2.5 py-1 rounded-full bg-[#E8F4FC] text-[#004AA9] border border-[#D8EBFE]"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {isActive && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-5 rounded-[24px] bg-[#E8F4FC] border border-[#D8EBFE]"
            >
              <p className="text-[#004AA9] text-sm font-medium mb-1">Assessment in progress</p>
              <p className="text-[#666666] text-xs leading-relaxed">
                Speak naturally. The AI is capturing key details from your responses automatically.
              </p>
            </motion.div>
          )}

          {isActive && (
            <div className="flex flex-col items-center gap-3 py-4">
              <MicVisualizer active={isActive} />
              <p className="text-[#999] text-xs">
                {conversation.isSpeaking ? 'AI speaking...' : 'Listening...'}
              </p>
            </div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm"
            >
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}

          <div className="flex flex-col gap-3 mt-auto">
            {isIdle && (
              <motion.button
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={startConversation}
                className="flex items-center justify-center gap-3 w-full py-4 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] active:scale-[0.98] pulse-glow bg-[#0B3D91] hover:bg-[#0a3377]"
              >
                <Mic size={20} />
                Start Voice Assessment
              </motion.button>
            )}

            {isConnecting && (
              <button
                disabled
                className="flex items-center justify-center gap-3 w-full py-4 rounded-xl font-semibold text-white bg-[#B8D4F0] cursor-not-allowed"
              >
                <Loader2 size={20} className="animate-spin" />
                Connecting...
              </button>
            )}

            {isActive && (
              <>
                <button
                  onClick={endConversation}
                  className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition-all"
                >
                  <PhoneOff size={18} />
                  End Conversation
                </button>

                <AnimatePresence>
                  {showGenerateButton && (
                    <motion.button
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      onClick={endConversation}
                      className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] bg-[#0B3D91] hover:bg-[#0a3377]"
                    >
                      <CheckCircle2 size={18} />
                      Generate My Results
                    </motion.button>
                  )}
                </AnimatePresence>
              </>
            )}

            {phase === 'done' && (
              <button
                disabled
                className="flex items-center justify-center gap-2 w-full py-4 rounded-xl font-semibold text-green-600 bg-green-50 border border-green-200"
              >
                <CheckCircle2 size={18} />
                Complete. Redirecting...
              </button>
            )}
          </div>

          {isIdle && (
            <p className="text-[#999] text-xs text-center flex items-center justify-center gap-1.5 -mt-2">
              <MicOff size={12} />
              Microphone required
            </p>
          )}
        </div>

        <div className="hidden md:flex flex-1 flex-col overflow-hidden bg-white">
          <div className="px-6 py-4 border-b border-[#D8EBFE] flex items-center justify-between">
            <h2 className="text-sm font-heading font-semibold text-[#004AA9]">Conversation Transcript</h2>
            {transcript.length > 0 && (
              <span className="text-xs text-[#999]">{transcript.length} messages</span>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-4 bg-[#FAFCFE]">
            {transcript.length === 0 ? (
              <div className="flex flex-col items-center justify-center flex-1 text-center gap-3">
                <div className="w-16 h-16 rounded-2xl bg-[#E8F4FC] border border-[#D8EBFE] flex items-center justify-center">
                  <Bot size={28} className="text-[#B8D4F0]" />
                </div>
                <div>
                  <p className="text-[#666666] font-medium">No messages yet</p>
                  <p className="text-[#999] text-sm mt-1">
                    Start the conversation to see your transcript here
                  </p>
                </div>
              </div>
            ) : (
              <>
                {transcript.map((msg, i) => (
                  <TranscriptBubble key={i} msg={msg} />
                ))}
                {isActive && !conversation.isSpeaking && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="self-start flex items-center gap-2 text-[#666666] text-xs px-4 py-2 bg-[#E8F4FC] rounded-full"
                  >
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0C91C6] opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#0C91C6]" />
                    </span>
                    Listening...
                  </motion.div>
                )}
                <div ref={transcriptEndRef} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}